import { FundingApplicationSchemas } from "api-types";
import { type IntlShape } from "react-intl";
import { getConnectorNameForFE } from "shared-ui";

import { MANUAL_AIR_MESSAGES } from "./manual-air-message-definitions";

type ManualAir = FundingApplicationSchemas.ManualAdditionalInformationRequestSchema;

const getMarketingPlatformNames = (air: ManualAir): string => {
  const data = air.data as FundingApplicationSchemas.TriageMarketingFailureAIRSchema | null;
  return data?.platforms_with_material_spend?.join(", ") || "marketing platforms";
};

const getReconnectPlatformName = (intl: IntlShape, air: ManualAir): string => {
  const data = air.data as FundingApplicationSchemas.ReconnectConnectionAIRSchema | null;
  if (data?.connection_identifier) {
    return getConnectorNameForFE(data.connection_identifier);
  }
  return intl.formatMessage(MANUAL_AIR_MESSAGES.defaultPlatformFallback);
};

export const getManualAirTitle = (intl: IntlShape, air: ManualAir): string => {
  switch (air.type) {
    case "rep_no_banking_data":
      return intl.formatMessage(MANUAL_AIR_MESSAGES.repNoBankingDataTitle);
    case "rep_triage_marketing_policy_failure":
      return intl.formatMessage(MANUAL_AIR_MESSAGES.repTriageMarketingTitle);
    case "reconnect_connection":
      return intl.formatMessage(MANUAL_AIR_MESSAGES.manualReconnectTitle, {
        platformType: getReconnectPlatformName(intl, air),
      });
    default:
      return intl.formatMessage(MANUAL_AIR_MESSAGES.defaultManualAirTitle);
  }
};

// Translation key backing the title, used as a stable `taskUniqueId` for the TaskCard.
export const getManualAirTitleKey = (air: ManualAir): string => {
  switch (air.type) {
    case "rep_no_banking_data":
      return MANUAL_AIR_MESSAGES.repNoBankingDataTitle.id;
    case "rep_triage_marketing_policy_failure":
      return MANUAL_AIR_MESSAGES.repTriageMarketingTitle.id;
    case "reconnect_connection":
      return MANUAL_AIR_MESSAGES.manualReconnectTitle.id;
    default:
      return MANUAL_AIR_MESSAGES.defaultManualAirTitle.id;
  }
};

export const getManualAirDescription = (intl: IntlShape, air: ManualAir): string => {
  switch (air.type) {
    case "rep_no_banking_data":
      return intl.formatMessage(MANUAL_AIR_MESSAGES.repNoBankingDataDescription);
    case "rep_triage_marketing_policy_failure":
      return intl.formatMessage(MANUAL_AIR_MESSAGES.repTriageMarketingDescription, {
        platformNames: getMarketingPlatformNames(air),
      });
    case "reconnect_connection":
      return intl.formatMessage(MANUAL_AIR_MESSAGES.manualReconnectDescription, {
        platformType: getReconnectPlatformName(intl, air),
      });
    default:
      return intl.formatMessage(MANUAL_AIR_MESSAGES.defaultManualAirDescription);
  }
};

export const getManualAirPanelBody = (intl: IntlShape, air: ManualAir): string => {
  switch (air.type) {
    case "rep_no_banking_data":
      // TODO: requiredMonths should come from the backend (e.g. 12 for RSS, 6 for standard).
      // The rep_no_banking_data AIR currently has no context schema — needs a backend change.
      return intl.formatMessage(MANUAL_AIR_MESSAGES.repNoBankingDataPanelBody, { requiredMonths: 6 });
    case "rep_triage_marketing_policy_failure":
      return intl.formatMessage(MANUAL_AIR_MESSAGES.repTriageMarketingPanelBody, {
        platformNames: getMarketingPlatformNames(air),
      });
    case "reconnect_connection":
      return intl.formatMessage(MANUAL_AIR_MESSAGES.manualReconnectPanelBody, {
        platformType: getReconnectPlatformName(intl, air),
      });
    default:
      return intl.formatMessage(MANUAL_AIR_MESSAGES.defaultManualAirPanelBody);
  }
};
